import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import SimpleLink from 'core/components/SimpleLink'
import TimePicklist, { TIME_RANGES } from 'core/components/widgets/TimePicklist'
import Grid from 'core/elements/grid'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { listResmgrHosts } from '../../actions'
import { resmgrHostsById } from '../../selectors'
import VMHADetailsDrawer from '../host-details/VMHADetailsDrawer'
import { getTimeRangeFromString } from './timeHelpers'
import { VMHAEvent, VMHAHostStatus } from './types'
import {
  createVMHAHostStatus,
  formatVMHATimestamp,
  getFirstEventForHost,
  parseVMHAEventsResponse,
  parseVMHATaskDetailsResponse,
} from './vmhaHelpers'

interface VMHAPastEventsTableProps {
  hostId: string
}

const { vmha } = ApiClient.getInstance()

const VMHAPastEventsTable: React.FC<VMHAPastEventsTableProps> = ({ hostId }) => {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<VMHAEvent[]>([])
  const [selectedEvent, setSelectedEvent] = useState<VMHAHostStatus | undefined>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGES.last_7_days)

  // Fetch host list by Ids
  useListAction(listResmgrHosts)
  const resMgrHosts = useAppSelector(resmgrHostsById)

  const fetchEvents = useCallback(async () => {
    if (!hostId) return

    try {
      setLoading(true)
      const { start, end } = getTimeRangeFromString(selectedTimeRange)
      const response = await vmha.getVMHAEventsForHost(hostId, start, end)
      const eventsByHost = parseVMHAEventsResponse(response)

      const hostEvents = eventsByHost.get(hostId)
      if (!hostEvents || hostEvents.length === 0) {
        setEvents([])
        return
      }

      const parsedEvents: VMHAEvent[] = getFirstEventForHost(hostEvents, resMgrHosts).sort(
        (a, b) => b.eventTimestamp - a.eventTimestamp,
      )

      setEvents(parsedEvents)
    } catch (error) {
      console.error('Failed to fetch VMHA events for host:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [hostId, selectedTimeRange])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const handleViewDetails = async (event: VMHAEvent) => {
    try {
      setLoadingDetails(true)
      setDrawerOpen(true)
      const taskDetailsResponse = await vmha.getVMHATaskDetails(
        event.vmhaUuid,
        event.eventTimestamp,
      )
      const tasks = parseVMHATaskDetailsResponse(taskDetailsResponse)
      const hostStatus = createVMHAHostStatus(event, tasks)

      setSelectedEvent(hostStatus)
    } catch (error) {
      console.error('Failed to fetch VMHA task details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const columns: GridViewColumn<VMHAEvent>[] = useMemo(
    () => [
      {
        id: 'vmhaUuid',
        key: 'vmhaUuid',
        label: 'Event',
        render: (_: string, item: VMHAEvent) => <Text variant="body2">{item.vmhaUuid}</Text>,
      },
      {
        id: 'timestamp',
        key: 'timestamp',
        label: 'Start Timestamp',
        render: (_: string, item: VMHAEvent) => (
          <Text variant="body2">{formatVMHATimestamp(item.timestamp)}</Text>
        ),
      },
      {
        id: 'actions',
        key: 'actions',
        label: 'Actions',
        render: (_: string, item: VMHAEvent) => (
          <SimpleLink onClick={() => handleViewDetails(item)}>View Details</SimpleLink>
        ),
      },
    ],
    [handleViewDetails],
  )

  const handleTimeRangeChange = useCallback(
    (timeRange: string, _startTimeEpoch: number, _endTimeEpoch: number, _steps: string) => {
      setSelectedTimeRange(timeRange)
    },
    [],
  )

  const reloadAction = useCallback(
    (hostId: string) => {
      if (selectedEvent?.event) {
        handleViewDetails(selectedEvent.event)
      }
    },
    [selectedEvent],
  )

  return (
    <>
      <Grid
        label="VMHA Past Events"
        data={events}
        columns={columns}
        loading={loading}
        uniqueIdentifier="vmhaUuid"
        compact
        emptyContent={<Text variant="body2">No VMHA events found for this host</Text>}
        onRefresh={fetchEvents}
        extraToolbarContent={
          <TimePicklist onChange={handleTimeRangeChange} currentTimeRange={selectedTimeRange} />
        }
      />
      {drawerOpen && (
        <VMHADetailsDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false)
            setSelectedEvent(undefined)
          }}
          vmhaStatus={selectedEvent}
          loading={loadingDetails}
          afterRetry={reloadAction}
        />
      )}
    </>
  )
}

export default VMHAPastEventsTable
